<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { appendScriptToDocument } from '@ui-utils';

export interface Props {
  sdkToken: string;
  workflowId: string;
  isModal?: boolean;
  locale?: string;
}

interface Emits {
  (event: 'onfido:completed'): void;
}

const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
  isModal: false,
  locale: 'nl_NL',
});

const onfidoElement = ref<HTMLElement | null>(null);
const onfidoSdk = 'https://sdk.onfido.com/v14.39';

const initOnfido = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window.Onfido as any).init({
    token: props.sdkToken,
    workflowRunId: props.workflowId,
    language: props.locale,
    containerId: 'onfido-mount',
    translations: {
      nl: {
        complete: {
          outro: {
            body: 'Dit is alles wat we nodig hebben om uw identiteit te verifiëren. U kunt deze pagina nu sluiten.',
          },
        },
      },
      en: {
        complete: {
          outro: {
            body: 'This is all we need to verify your identity. You can now close this page.',
          },
        },
      },
    },
    theme: {
      name: 'light',
      config: {
        // General styles
        borderRadiusButton: '50px',
        borderStyleSurfaceModal: '0px',

        colorBackgroundIcon: 'transparent',
        colorBackgroundSurfaceModal: 'rgba(255,255,255,0.75)',
        colorBackgroundLinkActive: 'transparent',
        colorBackgroundLinkHover: 'transparent',
        colorBackgroundDocTypeButton: 'transparent',
        colorBorderLinkUnderline: '1px solid #87bb3e',
        colorContentBody: '#263238',
        colorContentTitle: '#263238',
        colorContentSubtitle: '#263238',

        googleFonts: ['Figtree'],
        fontFamilyBody: 'Figtree, \'Open Sans\', sans-serif',
        fontFamilyTitle: 'Figtree, \'Open Sans\', sans-serif',
        fontFamilySubtitle: 'Figtree, \'Open Sans\', sans-serif',
        fontSizeBody: '15px',
        fontSizeSubtitle: '16px',
        fontSizeTitle: '20px',
        fontWeightBody: 700,
        fontWeightTitle: 700,

        // Primary button
        colorBackgroundButtonPrimary: '#01CAFD',
        colorBackgroundButtonPrimaryHover: '#009ED3',
        colorBackgroundButtonPrimaryActive: '#007EAA',
        colorBackgroundButtonIconActive: '#f3f8fa',
        colorBorderButtonPrimary: 'transparent',
        colorBorderButtonPrimaryHover: 'transparent',
        colorBorderButtonPrimaryActive: 'transparent',
        colorBorderButtonPrimaryFocus: 'transparent',
        colorContentButtonPrimaryText: '#0B218E',

        // Secondary button
        colorBackgroundButtonSecondary: 'transparent',
        colorBackgroundButtonSecondaryHover: '#D4E9FF',
        colorBackgroundButtonSecondaryActive: 'transparent',
        colorBorderButtonSecondary: '#0B218E',
        colorBorderButtonSecondaryHover: '#0B218E',
        colorBorderButtonSecondaryActive: '#0B218E',
        colorContentButtonSecondaryText: '#0B218E',
        colorContentLinkTextHover: '#0B218E',

        // Document type button
        colorBorderDocTypeButton: '#83bfe7',
        colorBackgroundDocTypeButtonHover: '#f3f8fa',
        colorBackgroundDocTypeButtonActive: '#ffffff',
        colorBorderDocTypeButtonHover: '#004388',
        colorBorderDocTypeButtonActive: '#1a5ea5',
        colorContentDocTypeButton: '#263238',

        // Disclaimer
        colorBackgroundDisclaimer: '#f3f8fa',

        colorIcon: '#263238',
        colorContentInfoPill: '#263238',
        colorBackgroundInfoPill: '#f5f5f5',
        colorBackgroundSelector: '#ffffff',
        colorBackgroundDropdownItemHover: '#e6f4ff',
        colorContentDropdownItemHover: '#ffffff',
        colorInputOutline: '#83bfe7',
        colorContentButtonTertiaryText: '#004388',
        colorContentInput: '#263238',
        colorBackgroundInput: '#ffffff',
        colorBorderInput: '#83bfe7',
        colorBorderInputHover: '#87bb3e',
        colorBorderActionFocus: '#83bfe7',
      },
    },
    onComplete: () => { emit('onfido:completed') },
  });
};

const waitForOnfidoToBeAvailable = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (onfidoElement.value && window.Onfido as any) {
    initOnfido();
  } else {
    setTimeout(() => {
      waitForOnfidoToBeAvailable();
    }, 20);
  }
};

onMounted(() => {
  appendScriptToDocument(onfidoSdk);

  waitForOnfidoToBeAvailable();
});
</script>

<template>
  <div
    id="onfido-mount"
    ref="onfidoElement"
    data-test-id="onfido"
    class="max-h-full min-h-[660px] sm:min-h-[700px]"
    :class="isModal ? 'onfido-modal' : 'onfido-embedded'"
  />
</template>

<style>
.onfido-modal #onfido-sdk {
  min-height: calc(100dvh - 109px) !important;
}

.onfido-embedded #onfido-sdk {
  min-height: 0dvh !important;
}
</style>
